/**
 * NeighborhoodSections Component
 *
 * Renders collapsible sections for neighborhood details in the Overview tab:
 * - Building Info
 * - Amenities
 * - Unit Features
 * - Policies
 * - HOA
 * - Short-Term Rental
 * - Schools
 *
 * Location was removed (2026-10) - its one field anyone looked at
 * (walkScore) moved to the top-level stat tiles in NeighborhoodModal.ts.
 */
export class NeighborhoodSections {
    options;
    sectionState;
    constructor(options) {
        this.options = options;
        this.sectionState = {
            description: true,
            building: false,
            amenities: true,
            unitFeatures: false,
            policies: false,
            hoa: false,
            str: false,
            schools: false,
            ...options.initialSectionState,
        };
    }
    render() {
        const { neighborhood } = this.options;
        const info = neighborhood.info;
        let html = '';
        // Building Info Section
        const buildingHtml = this.renderBuildingContent(info?.building, neighborhood);
        if (buildingHtml) {
            html += this.renderSection('building', 'Building', buildingHtml);
        }
        // Amenities Section
        const amenitiesHtml = this.renderAmenitiesContent(neighborhood.amenities, info?.amenities);
        if (amenitiesHtml) {
            html += this.renderSection('amenities', 'Amenities', amenitiesHtml);
        }
        // Unit Features Section
        const unitFeaturesHtml = this.renderUnitFeaturesContent(info?.unitFeatures);
        if (unitFeaturesHtml) {
            html += this.renderSection('unitFeatures', 'Unit Features', unitFeaturesHtml);
        }
        // Policies Section
        const policiesHtml = this.renderPoliciesContent(info?.policies);
        if (policiesHtml) {
            html += this.renderSection('policies', 'Policies', policiesHtml);
        }
        // HOA Section
        const hoaHtml = this.renderHOAContent(info?.hoa);
        if (hoaHtml) {
            html += this.renderSection('hoa', 'HOA', hoaHtml);
        }
        // STR Section
        const strHtml = this.renderSTRContent(info?.shortTermRental, neighborhood.strAllowed);
        if (strHtml) {
            html += this.renderSection('str', 'Short-Term Rental', strHtml);
        }
        // Schools Section
        const schoolsHtml = this.renderSchoolsContent(info?.schools);
        if (schoolsHtml) {
            html += this.renderSection('schools', 'Schools', schoolsHtml);
        }
        this.options.container.innerHTML = html;
        this.attachEventListeners();
    }
    renderSection(id, title, content) {
        const isCollapsed = !this.sectionState[id];
        return `
      <div class="sm-neighborhood-section${isCollapsed ? ' collapsed' : ''}" data-section="${id}">
        <button class="sm-neighborhood-section-header">
          <span>${title}</span>
          <svg class="sm-neighborhood-section-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </button>
        <div class="sm-neighborhood-section-content">
          ${content}
        </div>
      </div>
    `;
    }
    renderBuildingContent(building, neighborhood) {
        const rows = [];
        // Property Type from neighborhood or building info
        const propertyType = neighborhood?.propertyType || building?.propertyType;
        if (propertyType) {
            const typeLabels = {
                'resort-condo': 'Resort Condo',
                'townhome': 'Townhome',
                'single-family': 'Single Family',
                'condo': 'Condo',
                'hotel-condo': 'Hotel Condo',
            };
            rows.push(this.detailRow('Property Type', typeLabels[propertyType] || propertyType));
        }
        // Year Built
        const yearBuilt = neighborhood?.yearBuilt || building?.yearBuilt;
        if (yearBuilt) {
            rows.push(this.detailRow('Year Built', String(yearBuilt)));
        }
        // Total Units
        const totalUnits = neighborhood?.totalUnits || building?.totalUnits;
        if (totalUnits) {
            rows.push(this.detailRow('Total Units', totalUnits.toLocaleString()));
        }
        if (building?.stories) {
            rows.push(this.detailRow('Stories', String(building.stories)));
        }
        if (building?.constructionType) {
            rows.push(this.detailRow('Construction', building.constructionType));
        }
        if (building?.gated || neighborhood?.gated) {
            rows.push(this.detailRow('Gated', 'Yes'));
        }
        if (building?.elevators) {
            rows.push(this.detailRow('Elevators', 'Yes'));
        }
        if (building?.securedEntry) {
            rows.push(this.detailRow('Secured Entry', 'Yes'));
        }
        if (building?.parkingSpacesPerUnit) {
            rows.push(this.detailRow('Parking Spaces/Unit', String(building.parkingSpacesPerUnit)));
        }
        return rows.length > 0 ? rows.join('') : '';
    }
    renderAmenitiesContent(amenities, amenitiesInfo) {
        const items = [];
        // Add string amenities
        if (amenities && amenities.length > 0) {
            items.push(...amenities);
        }
        // Add structured amenities
        if (amenitiesInfo) {
            if (amenitiesInfo.pool)
                items.push('Pool');
            if (amenitiesInfo.spa)
                items.push('Spa/Hot Tub');
            if (amenitiesInfo.fitnessCenter)
                items.push('Fitness Center');
            if (amenitiesInfo.tennis)
                items.push('Tennis');
            if (amenitiesInfo.pickleball)
                items.push('Pickleball');
            if (amenitiesInfo.clubhouse)
                items.push('Clubhouse');
            if (amenitiesInfo.golfCourseAccess)
                items.push('Golf Course Access');
            if (amenitiesInfo.restaurantOnSite)
                items.push('Restaurant On-Site');
            if (amenitiesInfo.concierge)
                items.push('Concierge');
            if (amenitiesInfo.onSiteManagement)
                items.push('On-Site Management');
            if (amenitiesInfo.bbqArea)
                items.push('BBQ Area');
            if (amenitiesInfo.beachEquipment)
                items.push('Beach Equipment');
            if (amenitiesInfo.businessCenter)
                items.push('Business Center');
            if (amenitiesInfo.evCharging)
                items.push('EV Charging');
        }
        // Deduplicate
        const uniqueItems = [...new Set(items)];
        if (uniqueItems.length === 0)
            return '';
        return `
      <div class="sm-neighborhood-amenities-grid">
        ${uniqueItems.map(item => `<span class="sm-neighborhood-amenity">${this.escapeHtml(item)}</span>`).join('')}
      </div>
    `;
    }
    renderUnitFeaturesContent(unitFeatures) {
        if (!unitFeatures)
            return '';
        const rows = [];
        if (unitFeatures.ac !== undefined) {
            rows.push(this.detailRow('Air Conditioning', unitFeatures.ac ? 'Yes' : 'No'));
        }
        if (unitFeatures.washerDryer) {
            const labels = {
                in_unit: 'In Unit',
                hookups: 'Hookups',
                shared: 'Shared',
                none: 'None',
            };
            rows.push(this.detailRow('Washer/Dryer', labels[unitFeatures.washerDryer] || unitFeatures.washerDryer));
        }
        if (unitFeatures.lanai !== undefined) {
            rows.push(this.detailRow('Lanai', unitFeatures.lanai ? 'Yes' : 'No'));
        }
        if (unitFeatures.parkingType) {
            const labels = {
                covered: 'Covered',
                uncovered: 'Uncovered',
                garage: 'Garage',
                street: 'Street',
            };
            rows.push(this.detailRow('Parking Type', labels[unitFeatures.parkingType] || unitFeatures.parkingType));
        }
        if (unitFeatures.garage !== undefined) {
            rows.push(this.detailRow('Garage', unitFeatures.garage ? 'Yes' : 'No'));
        }
        if (unitFeatures.ceilingFans !== undefined) {
            rows.push(this.detailRow('Ceiling Fans', unitFeatures.ceilingFans ? 'Yes' : 'No'));
        }
        if (unitFeatures.kitchenType) {
            const labels = {
                full: 'Full Kitchen',
                kitchenette: 'Kitchenette',
                none: 'None',
            };
            rows.push(this.detailRow('Kitchen', labels[unitFeatures.kitchenType] || unitFeatures.kitchenType));
        }
        if (unitFeatures.storage) {
            rows.push(this.detailRow('Storage', unitFeatures.storage));
        }
        return rows.length > 0 ? rows.join('') : '';
    }
    renderPoliciesContent(policies) {
        if (!policies)
            return '';
        const rows = [];
        if (policies.petsAllowed !== undefined) {
            rows.push(this.detailRow('Pets Allowed', policies.petsAllowed ? 'Yes' : 'No'));
        }
        if (policies.petRestrictions) {
            rows.push(this.detailRow('Pet Restrictions', policies.petRestrictions));
        }
        if (policies.ageRestricted !== undefined) {
            rows.push(this.detailRow('Age Restricted', policies.ageRestricted ? 'Yes' : 'No'));
        }
        return rows.length > 0 ? rows.join('') : '';
    }
    renderHOAContent(hoa) {
        if (!hoa)
            return '';
        const rows = [];
        if (hoa.feeRangeMonthly) {
            rows.push(this.detailRow('Monthly Fee Range', hoa.feeRangeMonthly));
        }
        if (hoa.includes && hoa.includes.length > 0) {
            rows.push(this.detailRow('HOA Includes', hoa.includes.join(', ')));
        }
        if (hoa.specialAssessments) {
            rows.push(this.detailRow('Special Assessments', hoa.specialAssessments));
        }
        if (hoa.reserveFundStatus) {
            rows.push(this.detailRow('Reserve Fund Status', hoa.reserveFundStatus));
        }
        if (hoa.litigationPending !== undefined && hoa.litigationPending !== null) {
            rows.push(this.detailRow('Litigation Pending', hoa.litigationPending ? 'Yes' : 'No'));
        }
        return rows.length > 0 ? rows.join('') : '';
    }
    renderSTRContent(str, strAllowed) {
        const rows = [];
        // Use top-level strAllowed if available
        if (strAllowed !== undefined) {
            rows.push(this.detailRow('STR Allowed', strAllowed ? 'Yes' : 'No'));
        }
        else if (str?.strEligible !== undefined) {
            rows.push(this.detailRow('STR Eligible', str.strEligible ? 'Yes' : 'No'));
        }
        if (str?.minatoyaList !== undefined) {
            rows.push(this.detailRow('Minatoya List', str.minatoyaList ? 'Yes' : 'No'));
        }
        if (str?.hotelZoned !== undefined) {
            rows.push(this.detailRow('Hotel Zoned', str.hotelZoned ? 'Yes' : 'No'));
        }
        if (str?.hoaAllowsStr !== undefined) {
            rows.push(this.detailRow('HOA Allows STR', str.hoaAllowsStr ? 'Yes' : 'No'));
        }
        if (str?.rentalLicenseRequired !== undefined) {
            rows.push(this.detailRow('License Required', str.rentalLicenseRequired ? 'Yes' : 'No'));
        }
        return rows.length > 0 ? rows.join('') : '';
    }
    // Fallback only - prefer school.url (SchoolDigger's own canonical link,
    // captured by workers/neighborhood-enrichment when available) whenever
    // present. This exists for data written before that field was captured:
    // SchoolDigger's `schoolid` is a 12-digit ID with a 2-digit state FIPS
    // prefix (e.g. "15" for Hawaii), but their public profile URL path uses
    // the shorter ID with that prefix stripped - confirmed against known
    // public Hawaii school URLs (10-digit paths), not independently verified
    // end-to-end since schooldigger.com blocks scripted requests (403) even
    // for a correct path. If links still 404, this heuristic needs revisiting.
    buildSchoolDiggerUrl(schoolId) {
        if (!schoolId)
            return null;
        const shortId = schoolId.length > 10 ? schoolId.slice(schoolId.length - 10) : schoolId;
        return `https://www.schooldigger.com/go/HI/schools/${encodeURIComponent(shortId)}/school.aspx`;
    }
    // Three separate rows (elementary/middle/high) rather than one blended
    // score - a family cares which specific school their kids would attend,
    // not a neighborhood-wide average. See workers/neighborhood-enrichment,
    // which populates this via nearest-school-per-level distance matching.
    renderSchoolsContent(schools) {
        if (!schools)
            return '';
        const rows = [];
        const levels = [
            { key: 'elementary', label: 'Elementary School' },
            { key: 'middle', label: 'Middle School' },
            { key: 'high', label: 'High School' },
        ];
        for (const { key, label } of levels) {
            const school = schools[key];
            if (!school)
                continue;
            const parts = [school.name];
            const details = [];
            if (school.rankStars !== undefined) {
                details.push(`${school.rankStars}★`);
            }
            if (school.distanceMiles !== undefined) {
                details.push(`${school.distanceMiles} mi`);
            }
            if (details.length > 0) {
                parts.push(`(${details.join(' · ')})`);
            }
            const valueText = parts.join(' ');
            const schoolUrl = school.url ?? this.buildSchoolDiggerUrl(school.schoolId);
            const value = schoolUrl
                ? `<a href="${this.escapeHtml(schoolUrl)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(valueText)}</a>`
                : this.escapeHtml(valueText);
            rows.push(this.detailRowHtml(label, value));
        }
        if (rows.length === 0)
            return '';
        // Ratings are SchoolDigger's own star methodology, not GreatSchools/
        // Niche/etc - those can and do disagree (confirmed directly: Kihei
        // Charter School reads "average" on GreatSchools but 1-star on
        // SchoolDigger). Say which source this is so nobody mistakes one
        // rating service's number for a universal truth.
        return `${rows.join('')}<span class="sm-neighborhood-schools-note">According to SchoolDigger. Other sources may differ. Talk to your agent for further insights.</span>`;
    }
    detailRow(label, value) {
        return `
      <div class="sm-neighborhood-detail-row">
        <span class="sm-neighborhood-detail-label">${label}</span>
        <span class="sm-neighborhood-detail-value">${this.escapeHtml(value)}</span>
      </div>
    `;
    }
    // Same markup as detailRow, but valueHtml is trusted/pre-built HTML (e.g.
    // an <a> link) rather than plain text - callers are responsible for
    // escaping any user/external-sourced text themselves before passing it in.
    detailRowHtml(label, valueHtml) {
        return `
      <div class="sm-neighborhood-detail-row">
        <span class="sm-neighborhood-detail-label">${label}</span>
        <span class="sm-neighborhood-detail-value">${valueHtml}</span>
      </div>
    `;
    }
    attachEventListeners() {
        const sections = this.options.container.querySelectorAll('.sm-neighborhood-section');
        sections.forEach((section) => {
            const header = section.querySelector('.sm-neighborhood-section-header');
            const sectionId = section.getAttribute('data-section');
            header?.addEventListener('click', () => {
                const isCollapsed = section.classList.toggle('collapsed');
                if (sectionId) {
                    this.sectionState[sectionId] = !isCollapsed;
                }
            });
        });
    }
    update(neighborhood) {
        this.options.neighborhood = neighborhood;
        this.render();
    }
    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
    destroy() {
        this.options.container.innerHTML = '';
    }
}
//# sourceMappingURL=NeighborhoodSections.js.map